/**
 * CivixAssistant.jsx — Phase 2C
 * Full chat interface with conversation history sidebar (like ChatGPT).
 * Backs onto /api/civix-assistant/chat with persisted AssistantConversation.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Sparkles, Send, Settings as Cog, Brain, Loader2, Plus, Trash2, Clock, ChevronRight, ExternalLink, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

// Underwriting mode removed — dedicated Underwriting Bot handles carrier-specific questions
const MODES = [
  { value: 'auto', label: 'Auto', icon: Sparkles, desc: 'Auto-detect: CRM or general questions' },
  { value: 'crm', label: 'CRM Help', icon: Cog, desc: 'How-to questions about CIVIX features' },
];

// Keywords that indicate an underwriting / carrier-specific question
const UW_KEYWORDS = [
  'carrier', 'underwriting', 'rate class', 'instant issue', 'simplified issue',
  'final expense', 'whole life', 'term life', 'iul', 'indexed universal',
  'americo', 'mutual of omaha', 'transamerica', 'ethos', 'instabrain',
  'a1c', 'diabetes', 'copd', 'tobacco', 'smoker', 'build chart',
  'approval likelihood', 'graded benefit', 'guaranteed issue', 'recommend carrier',
];

function isUnderwritingQuestion(text) {
  if (!text) return false;
  const lower = text.toLowerCase();
  return UW_KEYWORDS.some((kw) => lower.includes(kw));
}

export default function CivixAssistant() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('auto');
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [showUWBanner, setShowUWBanner] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi — I'm CIVIX. Ask me how to use any feature in this CRM. For carrier underwriting recommendations, use the Underwriting Bot." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const scrollRef = useRef(null);
  const qc = useQueryClient();

  const { data: topicsData } = useQuery({ queryKey: ['civix-assistant-topics'], queryFn: () => apiGet('/civix-assistant/topics') });
  // Filter out underwriting topics — those are handled by the dedicated Underwriting Bot
  const topics = (topicsData?.topics || []).filter((t) => t.mode !== 'underwriting');

  const { data: convosData, refetch: refetchConvos } = useQuery({
    queryKey: ['civix-assistant-convos'],
    queryFn: () => apiGet('/civix-assistant/conversations'),
  });
  const conversations = convosData?.data?.conversations || [];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  async function loadConversation(id) {
    if (id === activeConvoId) return;
    try {
      const r = await apiGet(`/civix-assistant/conversations/${id}`);
      const convo = r.data?.conversation;
      if (convo) {
        setActiveConvoId(id);
        setMode(convo.mode || 'auto');
        const msgs = (convo.messages || []).filter((m) => m.role !== 'system');
        if (msgs.length > 0) setMessages(msgs);
        else setMessages([{ role: 'assistant', content: "Hi — I'm CIVIX. What can I help you with?" }]);
      }
    } catch (e) { toast.error('Could not load conversation'); }
  }

  async function newConversation() {
    setActiveConvoId(null);
    setMessages([{ role: 'assistant', content: "Hi — I'm CIVIX. Ask me how to use any feature in this CRM, or any life-insurance underwriting question." }]);
    setInput('');
  }

  async function deleteConversation(id, e) {
    e.stopPropagation();
    try {
      await apiDel(`/civix-assistant/conversations/${id}`);
      qc.invalidateQueries({ queryKey: ['civix-assistant-convos'] });
      if (activeConvoId === id) newConversation();
      toast.success('Deleted');
    } catch (_) {}
  }

  async function send(text) {
    const content = (text || input).trim();
    if (!content || busy) return;

    // Server-side guard: if message looks like a carrier/underwriting question,
    // show redirect banner instead of sending to the generic assistant.
    if (isUnderwritingQuestion(content)) {
      setMessages((m) => [
        ...m,
        { role: 'user', content },
        {
          role: 'assistant',
          content: 'That looks like a carrier underwriting question. For accurate, grounded recommendations based on your uploaded carrier grids, please use the Underwriting Bot.',
          isUWRedirect: true,
        },
      ]);
      setInput('');
      setShowUWBanner(true);
      return;
    }

    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const r = await apiPost('/civix-assistant/chat', {
        messages: next.filter((m) => m.role !== 'system').slice(-20),
        mode,
        conversationId: activeConvoId || undefined,
      });
      if (r.degraded) toast('AI service is degraded — fallback shown.', { icon: '⚠️' });
      const newMsg = { role: 'assistant', content: r.reply || '(no reply)' };
      setMessages((m) => [...m, newMsg]);
      if (r.conversationId && r.conversationId !== activeConvoId) {
        setActiveConvoId(r.conversationId);
      }
      refetchConvos();
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally { setBusy(false); }
  }

  function pickTopic(t) {
    setMode(t.mode);
    send(t.q);
  }

  return (
    <div className="flex h-[calc(100vh-9rem)] max-h-[850px] gap-0 panel overflow-hidden rounded-xl">
      {/* Sidebar */}
      <div className={`flex-col border-r border-civix-border transition-all ${sidebarOpen ? 'w-64 flex' : 'w-0 hidden'}`}>
        <div className="p-3 border-b border-civix-border flex items-center justify-between">
          <span className="text-sm font-semibold text-white/80">History</span>
          <button onClick={newConversation} className="text-civix-purple hover:text-white transition">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.length === 0 && (
            <div className="text-xs text-white/30 text-center py-4">No conversations yet</div>
          )}
          {conversations.map((c) => (
            <button key={c._id} onClick={() => loadConversation(c._id)}
              className={`w-full text-left p-2 rounded-md text-sm transition group relative ${activeConvoId === c._id ? 'bg-civix-purple/20 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}>
              <div className="truncate pr-6">{c.title || 'Conversation'}</div>
              <div className="text-[10px] text-white/30 mt-0.5 flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                {new Date(c.updatedAt).toLocaleDateString()}
              </div>
              <button onClick={(e) => deleteConversation(c._id, e)}
                className="absolute right-1.5 top-1.5 opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </button>
          ))}
        </div>
      </div>

      {/* Main chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top controls */}
        <div className="flex items-center gap-2 p-3 border-b border-civix-border">
          <button onClick={() => setSidebarOpen((s) => !s)} className="text-white/40 hover:text-white transition mr-1">
            <ChevronRight className={`w-4 h-4 transition ${sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
          <div className="panel p-0.5 flex gap-0.5">
            {MODES.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.value} onClick={() => setMode(m.value)} title={m.desc}
                  className={`px-3 py-1.5 rounded-sm text-xs flex items-center gap-1.5 transition ${mode === m.value ? 'bg-civix-purple/25 text-white border border-civix-purple/50' : 'text-white/60 hover:text-white'}`}>
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                </button>
              );
            })}
          </div>
          <button onClick={newConversation} className="ml-auto text-xs text-white/50 hover:text-white flex items-center gap-1 btn-ghost">
            <Plus className="w-3.5 h-3.5" /> New chat
          </button>
        </div>

        {/* Topics (shown only when no messages) */}
        {messages.length <= 1 && topics.length > 0 && (
          <div className="p-4 border-b border-civix-border">
            <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Try one of these</div>
            <div className="grid grid-cols-2 gap-1.5">
              {topics.map((t, i) => (
                <button key={i} onClick={() => pickTopic(t)}
                  className="text-left p-2.5 rounded-md border border-white/5 hover:border-civix-purple/40 hover:bg-civix-purple/5 transition">
                  <div className="text-sm flex items-center gap-2"><span>{t.icon}</span><span className="truncate">{t.label}</span></div>
                  <div className="text-[10px] text-white/40 mt-0.5 capitalize">{t.mode}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Underwriting redirect banner */}
        {showUWBanner && (
          <div className="mx-4 mt-2 flex items-start gap-3 rounded-xl border border-civix-cyan/30 bg-civix-cyan/10 px-4 py-3">
            <Brain className="w-5 h-5 text-civix-cyan shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-civix-cyan font-semibold">Looking for underwriting recommendations?</p>
              <p className="text-xs text-white/60 mt-0.5">The Underwriting Bot uses your uploaded carrier grids to give grounded, bucketed recommendations — instant-issue first.</p>
            </div>
            <button
              onClick={() => navigate('/underwriting-bot')}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-white bg-civix-cyan/20 hover:bg-civix-cyan/30 border border-civix-cyan/40 rounded-lg px-3 py-1.5 transition"
            >
              Open Underwriting Bot <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} isUWRedirect={m.isUWRedirect} />)}
          {busy && (
            <div className="flex items-center gap-2 text-white/50 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-civix-border p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about how to use CIVIX…"
            className="input flex-1"
            disabled={busy}
          />
          <button type="submit" disabled={busy || !input.trim()} className="btn-primary inline-flex items-center gap-2">
            <Send className="w-4 h-4" /> Send
          </button>
        </form>
        <div className="text-[10px] text-white/30 text-center pb-2">
          CIVIX Assistant uses your tenant's AI provider. Don't paste PII you wouldn't share with that provider.
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, content, isUWRedirect }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-civix-purple/20 border border-civix-purple/30 rounded-lg rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap">{content}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUWRedirect ? 'bg-civix-cyan/20 border border-civix-cyan/40' : 'bg-linear-to-br from-civix-purple to-civix-cyan'}`}>
        {isUWRedirect ? <Brain className="w-4 h-4 text-civix-cyan" /> : <Sparkles className="w-4 h-4 text-white" />}
      </div>
      <div className="flex-1 max-w-[85%]">
        <div className={`rounded-lg rounded-tl-sm px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${isUWRedirect ? 'bg-civix-cyan/10 border border-civix-cyan/20 text-civix-cyan' : 'bg-white/5 border border-white/10'}`}>
          {content}
        </div>
      </div>
    </div>
  );
}
